import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject
} from '@angular/core';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { AvatarFactory } from 'src/app/factory/avatar-factory';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';

import { Channel, ServiceRoomJourneyVO, Status, TenantContactVO } from '../tenant-contact.types';
import { TenantContactJourneyComponent } from './tenant-contact-journey.component';

@Component({
  selector: 'tenant-contact-detail',
  templateUrl: './tenant-contact-detail.component.html',
  styleUrls: ['./tenant-contact-detail.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    NzAvatarModule,
    NzDrawerModule,
    NzIconModule,
    NzModalModule,
    NzTagModule,
    I18nPipe,
    TestIdDirective,
    TenantContactJourneyComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TenantContactDetailComponent implements OnChanges {
  @Input() visible = false;
  @Input() tenantId = '';
  @Input() contact: TenantContactVO | null = null;
  @Output() readonly close = new EventEmitter<void>();

  private readonly modal = inject(NzModalService);
  readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  readonly AvatarFactory = AvatarFactory;
  isMobileView = typeof window !== 'undefined' ? window.innerWidth <= 767 : false;
  hasProfileData = false;
  profileExpanded = false;

  get drawerWidth(): string | number {
    return this.isMobileView ? '100vw' : 880;
  }

  get displayName(): string {
    return this.contact?.name || this.contact?.alias || this.i18n.fanyi('common.unknown');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['contact']) return;
    this.hasProfileData = this.hasSupplementaryProfileData(this.contact);
    this.profileExpanded = this.hasProfileData;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.isMobileView = window.innerWidth <= 767;
  }

  onClose(): void {
    this.close.emit();
  }

  toggleProfile(): void {
    this.profileExpanded = !this.profileExpanded;
  }

  openRoom(room: ServiceRoomJourneyVO): void {
    this.modal.create({
      nzTitle: `${this.displayName} / ${room.serviceNumberName || '-'} / 溝通記錄`,
      nzWidth: this.isMobileView ? '100vw' : 1280,
      nzStyle: this.isMobileView ? { top: '0', paddingBottom: '0' } : { top: '24px' },
      nzBodyStyle: this.isMobileView
        ? { height: 'calc(100vh - 55px)', overflow: 'auto', padding: '14px 12px' }
        : { height: 'calc(100vh - 112px)', overflow: 'auto', padding: '20px 24px' },
      nzContent: TenantContactJourneyComponent,
      nzData: {
        tenantId: this.tenantId,
        contactId: this.contact?.id,
        customerAccountId: this.contact?.accountId,
        roomOnly: false,
        initialRoom: room
      },
      nzFooter: null,
      nzWrapClassName: this.isMobileView ? 'contact-session-modal-mobile' : 'contact-session-modal'
    });
  }

  formatTime(value?: number): string {
    return value ? new Date(value).toLocaleString() : '-';
  }

  getStatusColor(status?: Status): string {
    return status === Status.Enable ? 'green' : status === Status.Disable ? 'red' : 'default';
  }

  getStatusTextKey(status?: Status): string {
    switch (status) {
      case Status.Enable:
        return 'theme.status.enable';
      case Status.Disable:
        return 'theme.status.disable';
      case Status.Delete:
        return 'account.tenant.status.deleted';
      case Status.Audit:
        return 'theme.status.audit';
      case Status.New:
        return 'theme.status.new';
      default:
        return 'account.tenant.status.unknown';
    }
  }

  getChannelText(channel?: Channel): string {
    return channel ? this.i18n.fanyi(`tenant.contact.channel-value.${String(channel).toLowerCase()}`) : '-';
  }

  getTypeText(type?: string): string {
    return type ? this.i18n.fanyi(`tenant.contact.type-value.${type.toLowerCase()}`) : '-';
  }

  getChannelColor(channel?: Channel): string {
    const map: Record<string, string> = {
      Line: 'green',
      Facebook: 'blue',
      Google: 'geekblue',
      Instagram: 'magenta',
      Webchat: 'cyan',
      Admin: 'gold',
      Internal: 'purple'
    };
    return map[channel as string] || 'default';
  }

  private hasSupplementaryProfileData(contact: TenantContactVO | null): boolean {
    if (!contact) return false;
    return [
      contact.phone,
      contact.email,
      contact.languages,
      contact.gender,
      contact.birthday,
      contact.maritalStatus,
      contact.interests,
      contact.company,
      contact.companyDepartment,
      contact.companyDuty,
      contact.companyPhone,
      contact.companyEmail,
      contact.companyAddress
    ].some(value => this.hasMeaningfulValue(value));
  }

  private hasMeaningfulValue(value: unknown): boolean {
    if (Array.isArray(value)) return value.length > 0;
    const normalized = String(value ?? '')
      .trim()
      .toLowerCase();
    return normalized !== '' && normalized !== '-' && normalized !== 'unknown';
  }
}
